import React, { useState, useRef } from 'react';
import SeoHead from '../components/SEO/SeoHead';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import emailjs from "@emailjs/browser";
import { trackEvent } from '../services/analytics';

interface ContactInfo {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  const theme = useTheme();
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const handleInputChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    emailjs
      .send(
        "service_qw02tqo", // Service-ID aus EmailJS
        "template_0qyz1rd", // Template-ID aus EmailJS
        {
          name: contactInfo.name,
          email: contactInfo.email,
          phone: contactInfo.phone,
          message: contactInfo.message,
        },
        "JPdoEVW6lCu6J8POn" // Public Key aus EmailJS
      )
      .then(
        (result) => {
          // Google Analytics Event-Tracking für erfolgreiche Formularübermittlung
          trackEvent('Kontakt', 'Formular abgesendet', 'Kontaktformular');
          
          setSnackbar({
            open: true,
            message:
              "Vielen Dank für Ihre Nachricht! Wir melden uns in Kürze bei Ihnen.",
            severity: "success",
          });
          setContactInfo({
            name: "",
            email: "",
            phone: "",
            message: "",
          });
        },
        (error) => {
          // Google Analytics Event-Tracking für Fehler bei Formularübermittlung
          trackEvent('Kontakt', 'Formularfehler', 'Kontaktformular');
          
          setSnackbar({
            open: true,
            message:
              "Es gab ein Problem beim Senden der Nachricht. Bitte versuchen Sie es später erneut.",
            severity: "error",
          });
        }
      );
  };

  const contactDetails = [
    {
      icon: <EmailIcon sx={{ fontSize: 32 }} />,
      title: "E-Mail",
      value: "kontakt@pyzon.de",
      href: "mailto:kontakt@pyzon.de",
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 32 }} />,
      title: "Telefon",
      value: "+49 (0) 152 05341897",
      href: "tel:+4915205341897",
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor:
          theme.palette.mode === "dark" ? "background.default" : "grey.50",
        minHeight: "100vh",
      }}
    >
      <SeoHead
        title="Kontakt | Website Agentur Darmstadt | Webseite erstellen lassen | pyzon"
        description="Kontaktieren Sie pyzon für professionelle Website-Entwicklung in Darmstadt. Lassen Sie sich beraten, wie wir Ihre maßgeschneiderte Webseite erstellen können."
        keywords="Website Agentur Darmstadt Kontakt, Webseite erstellen lassen, Website Agentur Darmstadt, Website Beratung, Webentwicklung Anfrage"
        canonicalPath="/contact"
      />
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h1"
            align="center"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Kontakt
          </Typography>
          <Typography
            variant="h5"
            align="center"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mb: 8 }}
          >
            Haben Sie Fragen oder möchten Sie ein Projekt mit uns starten?
            Kontaktieren Sie uns - wir freuen uns auf Ihre Nachricht!
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card
                elevation={0}
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  height: "100%",
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        id="contact-name"
                        label="Name"
                        value={contactInfo.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        fullWidth
                        required
                        variant="outlined"
                        aria-labelledby="name-label"
                        InputLabelProps={{
                          id: "name-label",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="contact-email"
                        label="E-Mail"
                        type="email"
                        value={contactInfo.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        fullWidth
                        required
                        variant="outlined"
                        aria-labelledby="email-label"
                        InputLabelProps={{
                          id: "email-label",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="contact-phone"
                        label="Telefon"
                        value={contactInfo.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        fullWidth
                        variant="outlined"
                        aria-labelledby="phone-label"
                        InputLabelProps={{
                          id: "phone-label",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="contact-message"
                        label="Nachricht"
                        multiline
                        rows={6}
                        value={contactInfo.message}
                        onChange={(e) =>
                          handleInputChange("message", e.target.value)
                        }
                        fullWidth
                        required
                        variant="outlined"
                        aria-labelledby="message-label"
                        InputLabelProps={{
                          id: "message-label",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        aria-label="Nachricht senden"
                        sx={{
                          py: 1.5,
                          fontSize: "1.1rem",
                          borderRadius: "50px",
                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          boxShadow: `0 8px 24px ${theme.palette.primary.main}25`,
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 12px 28px ${theme.palette.primary.main}40`,
                          },
                          transition: "all 0.3s ease-in-out",
                        }}
                      >
                        Nachricht senden
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 4,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Kontaktinformationen
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {contactDetails.map((detail) => (
                      <Box
                        key={detail.title}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.primary.main,
                            p: 1.5,
                            borderRadius: "12px",
                            background:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.05)"
                                : "rgba(0, 0, 0, 0.03)",
                          }}
                        >
                          {detail.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            {detail.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            component="a"
                            href={detail.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "text.secondary",
                              textDecoration: "none",
                              "&:hover": {
                                color: "primary.main",
                              },
                            }}
                          >
                            {detail.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ mt: 6 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      Öffnungszeiten
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      Montag - Freitag: 9:00 - 18:00 Uhr
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Samstag & Sonntag: Geschlossen
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
